import React, { useEffect, useRef, useState } from 'react';
import JsBarcode from 'jsbarcode';
import { getTotalPiezasProducto } from '../../api.wms.inventario';
import { ClipboardCopy, CheckCircle } from 'lucide-react';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

const ProductDetailsDrawer = ({ producto, onClose, clientes = [] }) => {
  const barcodeRef = useRef(null);
  const [stockTotal, setStockTotal] = useState(null);
  const [copied, setCopied] = useState(false);
  const [barcodeType, setBarcodeType] = useState('cb'); // 'cb' = código de barras, 'sku' = SKU interno

  useEffect(() => {
    if (producto && barcodeRef.current) {
      let valor = null;
      if (barcodeType === 'cb') {
        valor = producto.codigo_barras;
      } else if (barcodeType === 'sku') {
        valor = producto.codigo_producto;
      }
      if (valor) {
        JsBarcode(barcodeRef.current, valor, { format: 'CODE128', width: 2, height: 40, displayValue: true });
      } else {
        barcodeRef.current.innerHTML = '';
      }
    }
  }, [producto, barcodeType]);

  useEffect(() => {
    if (producto && producto.id) {
      getTotalPiezasProducto(producto.id)
        .then(setStockTotal)
        .catch(() => setStockTotal('N/A'));
    } else {
      setStockTotal('N/A');
    }
  }, [producto]);

  if (!producto) return null;

  // Buscar nombre del cliente por cuenta_id
  let clienteNombre = '';
  if (producto.cuenta_id && Array.isArray(clientes)) {
    const clienteObj = clientes.find(c => c.id === producto.cuenta_id);
    clienteNombre = clienteObj ? clienteObj.nombre : producto.cuenta_id;
  }

  // Función para copiar el SKU interno
  const handleCopySku = () => {
    navigator.clipboard.writeText(producto.codigo_producto);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  const handleBarcodeType = (event, newType) => {
    if (newType !== null) setBarcodeType(newType);
  };

  return (
    <div className="fixed inset-0 right-0 bg-black bg-opacity-30 flex justify-end z-50">
      <div className="bg-white w-full max-w-md h-full shadow-lg p-0 md:p-6 overflow-y-auto flex flex-col">
        <div className="flex justify-between items-center p-4 md:p-0">
          <button className="btn-secondary" onClick={onClose}>Cerrar</button>
        </div>
        <div className="px-4 md:px-0">
          {/* SKU interno y copiar */}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest">SKU interno</span>
            <span className="font-mono text-base bg-gray-100 rounded px-2 py-1 select-all">{producto.codigo_producto}</span>
            <button
              type="button"
              className="ml-1 p-1 rounded hover:bg-gray-200 transition"
              onClick={handleCopySku}
              title="Copiar SKU"
            >
              {copied ? <CheckCircle className="w-4 h-4 text-green-500" /> : <ClipboardCopy className="w-4 h-4 text-gray-500" />}
            </button>
          </div>
          <h2 className="text-2xl font-bold mb-1 text-blue-800">{producto.descripcion}</h2>
          <div className="text-sm text-gray-600 mb-4">{clienteNombre && <span className="font-semibold">{clienteNombre}</span>}</div>

          {/* Datos principales en tarjetas */}
          <div className="grid grid-cols-1 gap-3 mb-4">
            <div className="rounded-lg bg-gray-50 p-3 border border-gray-200">
              <div className="flex justify-between items-center mb-1">
                <span className="font-semibold text-gray-700">Estado</span>
                <span className={producto.activo ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>{producto.activo ? 'Activo' : 'Inactivo'}</span>
              </div>
              <div className="flex justify-between items-center mb-1">
                <span className="font-semibold text-gray-700">Stock</span>
                <span>{stockTotal !== null ? stockTotal : <span className="text-gray-400">Cargando...</span>}</span>
              </div>
              <div className="flex justify-between items-center mb-1">
                <span className="font-semibold text-gray-700">Unidad de medida</span>
                <span>{producto.unidad_medida || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center mb-1">
                <span className="font-semibold text-gray-700">Categoría</span>
                <span>{producto.familia || 'N/A'}</span>
              </div>
            </div>
            <div className="rounded-lg bg-gray-50 p-3 border border-gray-200">
              <div className="flex justify-between items-center mb-1">
                <span className="font-semibold text-gray-700">Peso (kg)</span>
                <span>{producto.peso_kg !== undefined && producto.peso_kg !== null ? producto.peso_kg : 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center mb-1">
                <span className="font-semibold text-gray-700">Volumen (m³)</span>
                <span>{producto.volumen_m3 !== undefined && producto.volumen_m3 !== null ? producto.volumen_m3 : 'N/A'}</span>
              </div>
            </div>
            <div className="rounded-lg bg-gray-50 p-3 border border-gray-200">
              <div className="flex justify-between items-center mb-1">
                <span className="font-semibold text-gray-700">Código de barras</span>
                <div className="flex-1 flex justify-end">
                  <ToggleButtonGroup
                    value={barcodeType}
                    exclusive
                    onChange={handleBarcodeType}
                    size="small"
                    color="primary"
                    sx={{ height: 28 }}
                  >
                    <ToggleButton
                      value="cb"
                      disabled={!producto.codigo_barras}
                      sx={{ fontSize: 11, px: 1.2, py: 0.2, minWidth: 0, height: 24, lineHeight: 1, borderRadius: 1 }}
                    >
                      Código de barras
                    </ToggleButton>
                    <ToggleButton
                      value="sku"
                      sx={{ fontSize: 11, px: 1.2, py: 0.2, minWidth: 0, height: 24, lineHeight: 1, borderRadius: 1 }}
                    >
                      SKU interno
                    </ToggleButton>
                  </ToggleButtonGroup>
                </div>
              </div>
              <div className="flex flex-col items-center mt-2 min-h-[60px]">
                <svg ref={barcodeRef}></svg>
                <span className="mt-1 font-mono text-xs text-gray-700">
                  {barcodeType === 'cb' ? (producto.codigo_barras || 'No registrado') : producto.codigo_producto}
                </span>
              </div>
            </div>
            {producto.imagen && (
              <div className="rounded-lg bg-gray-50 p-3 border border-gray-200 flex flex-col items-center">
                <img src={producto.imagen} alt="Imagen producto" className="w-32 h-32 object-cover mt-2" />
              </div>
            )}
          </div>

          {/* Datos adicionales en tarjetas */}
          <div className="mb-4">
            <h3 className="text-base font-bold mb-2 text-blue-700">Datos adicionales</h3>
            <div className="grid grid-cols-1 gap-3">
              {/* Características */}
              {(producto.color || producto.talla || producto.modelo) && (
                <div className="rounded-lg bg-gray-50 p-3 border border-gray-200">
                  <div className="font-semibold text-gray-700 mb-1">Características</div>
                  <ul className="ml-2 list-disc text-sm">
                    {producto.color && <li><b>Color:</b> {producto.color}</li>}
                    {producto.talla && <li><b>Talla:</b> {producto.talla}</li>}
                    {producto.modelo && <li><b>Modelo:</b> {producto.modelo}</li>}
                  </ul>
                </div>
              )}
              {/* Presentación y dimensiones */}
              {(producto.presentacion || producto.capacidad || producto.alto || producto.ancho || producto.largo) && (
                <div className="rounded-lg bg-gray-50 p-3 border border-gray-200">
                  <div className="font-semibold text-gray-700 mb-1">Presentación y dimensiones</div>
                  <ul className="ml-2 list-disc text-sm">
                    {producto.presentacion && <li><b>Presentación:</b> {producto.presentacion}</li>}
                    {producto.capacidad && <li><b>Capacidad:</b> {producto.capacidad}</li>}
                    {producto.alto && <li><b>Alto:</b> {producto.alto}</li>}
                    {producto.ancho && <li><b>Ancho:</b> {producto.ancho}</li>}
                    {producto.largo && <li><b>Largo:</b> {producto.largo}</li>}
                  </ul>
                </div>
              )}
              {/* Unidades y conversiones */}
              {(producto.piezas_por_tarima || producto.piezas_por_caja || producto.piezas_por_paquete) && (
                <div className="rounded-lg bg-gray-50 p-3 border border-gray-200">
                  <div className="font-semibold text-gray-700 mb-1">Unidades y conversiones</div>
                  <ul className="ml-2 list-disc text-sm">
                    {producto.piezas_por_tarima && <li><b>Piezas por tarima:</b> {producto.piezas_por_tarima}</li>}
                    {producto.piezas_por_caja && <li><b>Piezas por caja:</b> {producto.piezas_por_caja}</li>}
                    {producto.piezas_por_paquete && <li><b>Piezas por paquete:</b> {producto.piezas_por_paquete}</li>}
                  </ul>
                </div>
              )}
              {/* Otros datos */}
              {producto.observaciones && (
                <div className="rounded-lg bg-gray-50 p-3 border border-gray-200">
                  <div className="font-semibold text-gray-700 mb-1">Observaciones</div>
                  <div className="ml-2 text-sm">{producto.observaciones}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailsDrawer;
